import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { UsersApi } from '../api/users.api';
import type { AccountMembership } from '../api/team.types';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AccountAdminService {
  private readonly auth = inject(AuthService);
  private readonly users = inject(UsersApi);

  private readonly memberships = signal<readonly AccountMembership[] | null>(null);
  private readonly loading = signal(false);
  private trackedAccountId: number | null = null;

  readonly isLoading = this.loading.asReadonly();

  readonly isAccountAdmin = computed(() => {
    const accountId = this.auth.activeAccountId();
    const rows = this.memberships();
    if (accountId === null || !rows) {
      return false;
    }
    return rows.some((row) => membershipAccountId(row) === accountId && row.role === 'admin');
  });

  constructor() {
    effect(() => {
      if (!this.auth.isAuthenticated()) {
        this.clear();
        this.trackedAccountId = null;
        return;
      }
      const accountId = this.auth.activeAccountId();
      if (accountId !== this.trackedAccountId) {
        this.memberships.set(null);
        this.trackedAccountId = accountId;
      }
    });
  }

  async ensureLoaded(): Promise<boolean> {
    await this.auth.ensureActiveAccount();

    if (this.memberships() !== null) {
      return this.isAccountAdmin();
    }

    this.loading.set(true);
    try {
      const user = await this.users.meWithAccountMemberships();
      this.memberships.set(user.accountMemberships ?? []);
      return this.isAccountAdmin();
    } catch (err) {
      console.error('[account-admin] failed to load memberships', err);
      this.memberships.set([]);
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  clear(): void {
    this.memberships.set(null);
  }
}

function membershipAccountId(row: AccountMembership): number | null {
  const account = row.account;
  if (typeof account === 'number') {
    return account;
  }
  if (typeof account === 'object' && account !== null && 'id' in account) {
    return (account as { id: number }).id;
  }
  return null;
}
